import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_CURRENT_USER_PERMISSIONS, UserPermissionsData } from '../graphql/queries/auth';
import { createUserPermissions, UserPermissions } from '../utils/permissionEngine';
import { getAuthUserString } from '../helpers/auth_storage';
import {
  isCustomerPortalUser as checkCustomerPortalUser,
  isStaffUser as checkStaffUser,
  parseDatabaseId,
} from '../utils/customerAccess';

interface AuthUser {
  id: string;
  databaseId?: number;
  name: string;
  email: string;
}

interface AuthContextType {
  user: AuthUser | null;
  roles: Set<string>;
  capabilities: Set<string>;
  permissions: UserPermissions;
  loading: boolean;
  error: Error | null;
  
  // Helper methods
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasCapability: (capability: string) => boolean;
  hasAnyCapability: (capabilities: string[]) => boolean;
  hasAllCapabilities: (capabilities: string[]) => boolean;
  isSuperAdmin: () => boolean;
  isStaffUser: () => boolean;
  isCustomerPortalUser: () => boolean;
  
  // Refresh permissions
  refreshPermissions: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

function readStoredAuth(): {
  user: AuthUser | null;
  roles: Set<string>;
  capabilities: Set<string>;
} {
  const empty = {
    user: null as AuthUser | null,
    roles: new Set<string>(),
    capabilities: new Set<string>(),
  };
  const authUser = getAuthUserString();
  if (!authUser) return empty;
  try {
    const parsed = JSON.parse(authUser);
    return {
      user: parsed.user
        ? {
            id: parsed.user.id,
            databaseId: parseDatabaseId(parsed.user.id, parsed.user.databaseId),
            name: parsed.user.name,
            email: parsed.user.email,
          }
        : null,
      roles: new Set(parsed.roles || []),
      capabilities: new Set(parsed.capabilities || []),
    };
  } catch {
    return empty;
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const stored = readStoredAuth();
  const [user, setUser] = useState<AuthUser | null>(stored.user);
  const [roles, setRoles] = useState<Set<string>>(stored.roles);
  const [capabilities, setCapabilities] = useState<Set<string>>(stored.capabilities);

  // Fetch user permissions from GraphQL
  const { data, loading, error, refetch } = useQuery<UserPermissionsData>(
    GET_CURRENT_USER_PERMISSIONS,
    {
      errorPolicy: 'all', // Continue even if there are errors
      fetchPolicy: 'cache-and-network', // Always fetch fresh data
      skip: !getAuthUserString(), // Skip if not logged in
    }
  );

  // Update state when data changes
  useEffect(() => {
    if (data?.viewer) {
      const viewer = data.viewer;
      
      setUser({
        id: viewer.id,
        databaseId: parseDatabaseId(viewer.id, viewer.databaseId),
        name: viewer.name,
        email: viewer.email,
      });

      // Extract roles (WPGraphQL may return null roles for some JWT contexts)
      const userRoles = (viewer.roles?.nodes || [])
        .map((role) => role?.name)
        .filter((name): name is string => !!name);
      if (userRoles.length) {
        setRoles(new Set(userRoles));
      } else {
        const fallback = readStoredAuth();
        if (fallback.roles.size) setRoles(fallback.roles);
      }

      // Extract capabilities
      const userCapabilities = viewer.capabilities || [];
      if (userCapabilities.length) {
        setCapabilities(new Set(userCapabilities));
      } else {
        const fallback = readStoredAuth();
        if (fallback.capabilities.size) setCapabilities(fallback.capabilities);
      }
    } else {
      const fallback = readStoredAuth();
      if (fallback.user) setUser(fallback.user);
      if (fallback.roles.size) setRoles(fallback.roles);
      if (fallback.capabilities.size) setCapabilities(fallback.capabilities);
    }
  }, [data]);

  // Create permissions object
  const permissions = createUserPermissions(
    Array.from(roles),
    Array.from(capabilities)
  );

  // Helper methods
  const hasRole = (role: string): boolean => {
    return roles.has(role);
  };

  const hasAnyRole = (rolesArray: string[]): boolean => {
    return rolesArray.some(role => roles.has(role));
  };

  const hasCapability = (capability: string): boolean => {
    return capabilities.has(capability);
  };

  const hasAnyCapability = (capabilitiesArray: string[]): boolean => {
    return capabilitiesArray.some(cap => capabilities.has(cap));
  };

  const hasAllCapabilities = (capabilitiesArray: string[]): boolean => {
    return capabilitiesArray.every(cap => capabilities.has(cap));
  };

  const isSuperAdmin = (): boolean => {
    return roles.has('super_admin_business') || roles.has('administrator');
  };

  const isStaffUser = (): boolean => {
    return checkStaffUser(permissions);
  };

  const isCustomerPortalUser = (): boolean => {
    return checkCustomerPortalUser(permissions);
  };

  const refreshPermissions = () => {
    refetch();
  };

  const value: AuthContextType = {
    user,
    roles,
    capabilities,
    permissions,
    loading,
    error: error as Error | null,
    hasRole,
    hasAnyRole,
    hasCapability,
    hasAnyCapability,
    hasAllCapabilities,
    isSuperAdmin,
    isStaffUser,
    isCustomerPortalUser,
    refreshPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use AuthContext
 * @throws Error if used outside AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
